import { GenericAlert } from "@studio3t/generic-components";
import {
  createContext,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import {
  leaveModalKeepLabel,
  leaveModalMessage,
} from "@/components/leaveModalCopy";
import {
  SAVE_DRAFT_MODAL,
  SaveWorkspaceModal,
} from "@/pages/NewStudio/SaveWorkspaceModal";

// The unsaved-work guard (Figma Modal 14896-126480): while the studio holds
// work that isn't saved, every way out — the nav rail, the logo, the studio
// switch, opening another pipeline, Clear, a page refresh — first asks.
// The studio registers what is unsaved (and how to save it); the places
// that navigate run their move through `confirm`, which either lets it
// through at once or holds it behind the modal (Figma 15949-170754 /
// 15949-174922): Save and leave (scratch work first names its draft in
// the "Save your work as a draft" modal, 15949-170609), Continue without
// saving, Cancel. A refresh or tab close gets the browser's own dialog on
// the same condition — the only kind a page is allowed there.

export interface UnsavedWork {
  /** A draft with unsaved changes, scratch work never saved as one, or a
   *  deployed pipeline with local edits (nothing to save them to). */
  kind: "draft" | "scratch" | "deployed";
  /** The draft's name; scratch has none. */
  name: string | null;
  /** Saves the open draft over itself; resolves null, or the reason it
   *  couldn't. Only a draft has this: scratch needs a name first (saveAs),
   *  a deployed pipeline's local edits have nothing to save to. */
  save: (() => Promise<string | null>) | null;
  /** Saves scratch work as a new draft under the name the user typed;
   *  resolves null, or the reason it couldn't. Scratch only. */
  saveAs: ((name: string) => Promise<string | null>) | null;
  /** Saves the current work as a fresh draft copy (auto-named); resolves
   *  null, or the reason it couldn't. The switch-pipeline modal's
   *  "Duplicate and continue" (Figma 14896-126480) — a deployed
   *  pipeline's local edits only; drafts and scratch save instead. */
  duplicate: (() => Promise<string | null>) | null;
}

/** A move held behind the modal, with what it opens when that is a
 *  pipeline switch — the switch modal is titled after the target. */
interface PendingMove {
  proceed: () => void;
  opening?: string;
}

interface LeaveGuardValue {
  unsaved: UnsavedWork | null;
  register: (work: UnsavedWork | null) => void;
  /** Runs `proceed` now, or after the modal's answer. `opening` names the
   *  pipeline a switch is about to open — with it (and something to
   *  duplicate) the modal is the switch variant. */
  confirm: (proceed: () => void, opening?: string) => void;
}

const LeaveGuardContext = createContext<LeaveGuardValue | null>(null);

export function LeaveGuardProvider({ children }: { children: ReactNode }) {
  const [unsaved, setUnsaved] = useState<UnsavedWork | null>(null);
  // The move waiting on the modal's answer.
  const [pending, setPending] = useState<PendingMove | null>(null);

  const register = useCallback((work: UnsavedWork | null) => {
    setUnsaved(work);
  }, []);
  const confirm = useCallback(
    (proceed: () => void, opening?: string) => {
      if (unsaved) setPending({ proceed, opening });
      else proceed();
    },
    [unsaved],
  );

  const value = useMemo(
    () => ({ unsaved, register, confirm }),
    [unsaved, register, confirm],
  );
  return (
    <LeaveGuardContext.Provider value={value}>
      {children}
      <UnsavedWorkModal
        unsaved={unsaved}
        pending={pending}
        onDone={() => setPending(null)}
      />
    </LeaveGuardContext.Provider>
  );
}

function useLeaveGuardContext(): LeaveGuardValue {
  const ctx = useContext(LeaveGuardContext);
  if (!ctx) throw new Error("useLeaveGuard requires LeaveGuardProvider");
  return ctx;
}

/** The guard for anything that leaves the current work behind. */
export function useLeaveGuard() {
  const { unsaved, confirm } = useLeaveGuardContext();
  return { dirty: unsaved !== null, confirm };
}

/** `navigate` that asks first while work is unsaved. */
export function useGuardedNavigate() {
  const navigate = useNavigate();
  const { confirm } = useLeaveGuardContext();
  return useCallback(
    (to: string) => confirm(() => navigate(to)),
    [confirm, navigate],
  );
}

/** A link's onClick that holds the link's own navigation behind the guard
 *  (a plain click goes through untouched while nothing is unsaved). */
export function useGuardedLinkClick() {
  const { unsaved } = useLeaveGuardContext();
  const guardedNavigate = useGuardedNavigate();
  return useCallback(
    (to: string) => (e: ReactMouseEvent) => {
      if (!unsaved) return;
      e.preventDefault();
      guardedNavigate(to);
    },
    [unsaved, guardedNavigate],
  );
}

/** A keep-the-work action (save/save-as/duplicate), callable through a
 *  stable identity that always runs the LATEST closure — the closures
 *  change every render, and the registration must not. Identity only
 *  flips with the action's presence. */
function useLatestAction<A extends unknown[]>(
  action: ((...args: A) => Promise<string | null>) | null,
): ((...args: A) => Promise<string | null>) | null {
  const ref = useRef(action);
  ref.current = action;
  const call = useCallback(
    (...args: A) => ref.current?.(...args) ?? Promise.resolve(null),
    [],
  );
  return action === null ? null : call;
}

/** What the studio has unsaved right now (null: nothing). The action
 *  closures are read at answer time, so they may close over live state. */
export function useRegisterUnsavedWork(work: UnsavedWork | null) {
  const { register } = useLeaveGuardContext();
  const save = useLatestAction(work?.save ?? null);
  const saveAs = useLatestAction(work?.saveAs ?? null);
  const duplicate = useLatestAction(work?.duplicate ?? null);
  const kind = work?.kind ?? null;
  const name = work?.name ?? null;
  useEffect(() => {
    if (kind === null) return;
    register({ kind, name, save, saveAs, duplicate });
    return () => register(null);
  }, [kind, name, save, saveAs, duplicate, register]);
}

// The modal's answers. A keep-the-work action (Save / Duplicate) runs
// the registered closure and leaves on success; a reason it couldn't (or
// a thrown error) stays in the modal.
function useUnsavedWorkAnswers(
  unsaved: UnsavedWork | null,
  pending: PendingMove | null,
  onDone: () => void,
) {
  const [busy, setBusy] = useState<"Save" | "Duplicate" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const leave = () => {
    onDone();
    setError(null);
    pending?.proceed();
  };
  const cancel = () => {
    setError(null);
    onDone();
  };
  const runAndLeave = async (
    what: "Save" | "Duplicate",
    action: (() => Promise<string | null>) | null,
  ) => {
    if (!action) return leave();
    setBusy(what);
    setError(null);
    try {
      const reason = await action();
      if (reason) setError(reason);
      else leave();
    } catch (e) {
      setError(`${what} failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };
  return {
    saving: busy === "Save",
    duplicating: busy === "Duplicate",
    error,
    leave,
    cancel,
    saveAndLeave: () => runAndLeave("Save", unsaved?.save ?? null),
    duplicateAndLeave: () =>
      runAndLeave("Duplicate", unsaved?.duplicate ?? null),
  };
}

// Which face the held move gets: switching pipelines over a DEPLOYED
// pipeline's local edits is the one case whose modal offers Duplicate
// (Figma 14896-126480) — duplicating is the only way to keep edits that
// belong to a deployed pipeline. Everything else asks to Save.
function isDeployedSwitch(pending: PendingMove, unsaved: UnsavedWork): boolean {
  if (pending.opening === undefined) return false;
  return unsaved.kind === "deployed" && unsaved.duplicate !== null;
}

/** The switch modal names its destination in the header (Figma
 *  14896-126481: "Open <target>"). */
function switchTitle(pending: PendingMove): string {
  return `Open ${pending.opening}`;
}

interface UnsavedWorkModalProps {
  unsaved: UnsavedWork;
  pending: PendingMove;
  onDone: () => void;
}

// The switch modal: titled after the target, Continue discards,
// "Duplicate and continue" saves the work as a copy first. The modal's
// `loading` rides the duplicate: Continue disables under its own label
// while the extra action reports the progress.
function SwitchPipelineModal({
  unsaved,
  pending,
  onDone,
}: UnsavedWorkModalProps) {
  const answers = useUnsavedWorkAnswers(unsaved, pending, onDone);
  return (
    <ConfirmDeleteModal
      isOpen
      title={switchTitle(pending)}
      confirmType="primary"
      confirmLabel="Continue"
      maxWidth={600}
      extraActionSide="left"
      loading={answers.duplicating}
      loadingLabel="Continue"
      error={answers.error}
      extraAction={{
        label: answers.duplicating ? "Duplicating…" : "Duplicate and continue",
        onClick: () => void answers.duplicateAndLeave(),
      }}
      message={
        <GenericAlert
          state="Warning"
          icon
          dismissible={false}
          message="Continuing erases everything you have worked on so far. Duplicate as draft to save your progress before proceeding."
        />
      }
      onConfirm={answers.leave}
      onCancel={answers.cancel}
    />
  );
}

// The save modal (Figma 15949-170754 draft / 15949-174922 scratch), the
// same face for every way out — navigation, Clear, opening another
// pipeline: the keep-the-work action alone on the left — "Save and
// leave" for a draft, "Save as draft and leave" for scratch — and Cancel
// + the primary Continue on the right, the Figma order the switch modal
// above also keeps. Continue discards.
// A draft's keep action saves in place; scratch work has no name yet, so
// its keep action swaps this modal for the "Save your work as a draft"
// name prompt (Figma 15949-170609) and leaves once that save lands —
// cancelling the prompt drops the move altogether, back to the studio
// with no modal up. Work with nothing registered to save it (a deployed
// pipeline's local edits) has no keep action: Cancel + Continue only.
function SaveWorkModal({ unsaved, pending, onDone }: UnsavedWorkModalProps) {
  const answers = useUnsavedWorkAnswers(unsaved, pending, onDone);
  const [naming, setNaming] = useState(false);

  if (naming && unsaved.saveAs !== null) {
    const saveAs = unsaved.saveAs;
    return (
      <SaveWorkspaceModal
        isOpen
        {...SAVE_DRAFT_MODAL}
        onClose={answers.cancel}
        onSave={async (name) => {
          const reason = await saveAs(name);
          if (reason) throw new Error(reason);
          answers.leave();
        }}
      />
    );
  }

  const keep =
    unsaved.kind === "draft"
      ? {
          label: answers.saving ? "Saving…" : leaveModalKeepLabel("draft"),
          onClick: () => void answers.saveAndLeave(),
        }
      : unsaved.kind === "scratch"
        ? {
            label: leaveModalKeepLabel("scratch"),
            onClick: () => setNaming(true),
          }
        : undefined;
  return (
    <ConfirmDeleteModal
      isOpen
      title="Unsaved changes"
      confirmType="primary"
      confirmLabel="Continue"
      maxWidth={600}
      extraActionSide="left"
      loading={answers.saving}
      loadingLabel="Continue"
      error={answers.error}
      extraAction={keep}
      message={
        <GenericAlert
          state="Warning"
          icon
          dismissible={false}
          message={leaveModalMessage(unsaved)}
        />
      }
      onConfirm={answers.leave}
      onCancel={answers.cancel}
    />
  );
}

function UnsavedWorkModal({
  unsaved,
  pending,
  onDone,
}: {
  unsaved: UnsavedWork | null;
  pending: PendingMove | null;
  onDone: () => void;
}) {
  if (pending === null || unsaved === null) return null;
  const props = { unsaved, pending, onDone };
  return isDeployedSwitch(pending, unsaved) ? (
    <SwitchPipelineModal {...props} />
  ) : (
    <SaveWorkModal {...props} />
  );
}
